/**
 * Property Dashboard — shell-first render gating
 * ---------------------------------------------
 * Get data from usePropertyDashboardData → render sections. No business logic or thresholds here.
 * Shell renders as soon as app-context + property/plant are ready; skeletons until telemetry.
 */
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Zap, Plus, Download, Moon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow, format } from 'date-fns';
import { EnergyPosition } from '@/components/dashboard/EnergyPosition';
import { EnergyFlow } from '@/components/dashboard/EnergyFlow';
import { EnergyOutlook } from '@/components/dashboard/EnergyOutlook';
import { OvernightForecastCard } from '@/components/dashboard/OvernightForecastCard';
import { EnergyGuidanceCard } from '@/components/dashboard/EnergyGuidanceCard';
import { TelemetryTimeline } from '@/components/dashboard/TelemetryTimeline';
import {
  SkeletonEnergyPosition,
  SkeletonEnergyFlow,
  SkeletonChart,
} from '@/components/dashboard/SkeletonLoader';
import { toast } from 'sonner';
import { showReadinessBanner, type ReadinessState } from '@/lib/readiness';
import { type PollStatus } from '@/lib/poll-status';
import { readinessCopy, pollStatusOfflineCopy } from '@/lib/inline-copy';
import { t } from '@/lib/vocabulary';
import { addRecentPropertyId } from '@/lib/recent-properties';
import {
  normalizeBatteryPowerForDisplay,
  getGridKwForDisplay,
  getLoadKwForEnergyFlow,
  getBatteryStateForDisplay,
  getSystemEnergyStateDisplay,
} from '@/lib/energy-state';
import { usePropertyDashboardData } from '@/hooks/usePropertyDashboardData';
import { useDominantPowerState } from '@/hooks/useDominantPowerState';
import { useCurtailment } from '@/hooks/useCurtailment';
import { useSimilarDayForecast } from '@/hooks/useSimilarDayForecast';
import { CurtailmentCard, formatCurtailmentDollars } from '@/components/dashboard/CurtailmentCard';
import { CurtailmentSummary } from '@/components/dashboard/CurtailmentSummary';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { HomeownerBillingCard } from '@/components/billing/HomeownerBillingCard';
import { ResellerSetupDialog } from '@/components/billing/ResellerSetupDialog';
import { GatewayStatusCard } from '@/components/gateway/GatewayStatusCard';
import { AddGatewayDialog } from '@/components/gateway/AddGatewayDialog';
import { useGatewayDevice } from '@/hooks/useGatewayDevice';
import { useVisits } from '@/hooks/useVisits';
import { ScheduleVisitDialog } from '@/components/visits/ScheduleVisitDialog';
import { CustomerReportSettings } from '@/components/property/CustomerReportSettings';
import { WarrantyRegistryCard } from '@/components/warranty/WarrantyRegistryCard';
import { AddWarrantyDialog } from '@/components/warranty/AddWarrantyDialog';
import { OptimizationInsightsCard } from '@/components/dashboard/OptimizationInsightsCard';
import { usePropertyCapabilities } from '@/hooks/usePropertyCapabilities';
import { HomeownerPlanCard } from '@/components/property/HomeownerPlanCard';
import { ReportIssueDialog } from '@/components/property/ReportIssueDialog';
import { MyReportedIssues } from '@/components/property/MyReportedIssues';
import { HomeownerServiceActivity } from '@/components/property/HomeownerServiceActivity';
import type { ForecastData } from '@/types/dashboard';
import { buildDashboardForecastViewModel, logForecastViewModel, type DashboardForecastViewModel } from '@/lib/dashboard-forecast';

/** Maps PollStatus to display config for StatusBadge. live -> Live, stale -> Stale/Data outdated, else -> Offline. */
function getPollStatusDisplay(status: PollStatus, isHomeowner?: boolean): { label: string; className: string; dot: string } {
  switch (status) {
    case 'live':
      return { label: 'Live', className: 'bg-status-online/10 text-status-online border-status-online/20', dot: 'bg-status-online' };
    case 'stale':
      return { label: isHomeowner ? 'Data outdated' : 'Stale', className: 'bg-status-stale/10 text-status-stale border-status-stale/20', dot: 'bg-status-stale' };
    default:
      return { label: 'Offline', className: 'bg-status-offline/10 text-status-offline border-status-offline/20', dot: 'bg-status-offline' };
  }
}

function StatusBadge({ status, isHomeowner }: { status: PollStatus; isHomeowner?: boolean }) {
  const c = getPollStatusDisplay(status, isHomeowner);
  return (
    <Badge variant="outline" className={`text-sm ${c.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${c.dot}`} />
      {c.label}
    </Badge>
  );
}

/** Forecast right column: gated by readiness. Always mounts EnergyOutlook when ready so chart never unmounts; uses last valid data when loading/error. */
function ForecastRightColumn({
  propertyId,
  selectedPlantId,
  forecastData,
  timeToSunrise,
  netPower,
  selectedProperty,
  batteryCapacityKwh,
  /** Current measured energy remaining (kWh). Chart series is state-anchored: first point = this value. */
  currentEnergyRemainingKwh,
  readinessState,
  loading = false,
  error = null,
  onRetry,
  forecastViewModel,
}: {
  propertyId: string | undefined;
  selectedPlantId: string | null;
  forecastData: ForecastData | null;
  timeToSunrise: string;
  netPower: number;
  selectedProperty: { battery_kwh_total?: number; battery_reserve_pct?: number } | undefined;
  /** Capacity for Y-axis and kWh display (effective when available, else configured). */
  batteryCapacityKwh: number;
  /** Current measured energy remaining (kWh). If provided, forecast chart is anchored so series[0].expected === this. */
  currentEnergyRemainingKwh?: number | null;
  readinessState: ReadinessState;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Canonical forecast view model — sunrise values used instead of local recomputation. */
  forecastViewModel?: DashboardForecastViewModel | null;
}) {
  const similarDay = useSimilarDayForecast(propertyId, selectedPlantId, 24);
  const reservePct = (selectedProperty?.battery_reserve_pct ?? 20) / 100;
  const reserveKwh = reservePct * batteryCapacityKwh;

  // Must run unconditionally so hook count is stable (avoids React "Rendered more hooks than during the previous render").
  const chartDataPoints = useMemo(() => {
    if (!forecastData?.chartData) return [];
    const chartData = forecastData.chartData as {
      timestamps?: string[];
      socExpected?: number[];
      socBest?: number[];
      socWorst?: number[];
    };
    const timestamps = chartData.timestamps ?? [];
    const socExpectedSample = chartData.socExpected ?? [];
    const socMax = socExpectedSample.length ? Math.max(...socExpectedSample) : 0;
    const isSocDecimal = socMax > 0 && socMax <= 1.5;
    const toKwh = (soc: number) =>
      isSocDecimal ? (soc ?? 0) * batteryCapacityKwh : ((soc ?? 0) * batteryCapacityKwh) / 100;
    const raw = timestamps.map((ts: string, i: number) => {
      const expected = toKwh(chartData.socExpected?.[i] ?? 0);
      const best = toKwh(chartData.socBest?.[i] ?? 0);
      const worst = toKwh(chartData.socWorst?.[i] ?? 0);
      return {
        time: format(new Date(ts), 'HH:mm'),
        timestamp: new Date(ts).getTime(),
        expected,
        best,
        worst,
        bandHeight: Math.max(0, best - worst),
      };
    });
    if (raw.length === 0) return raw;
    const anchor = currentEnergyRemainingKwh;
    if (anchor == null || typeof anchor !== 'number' || !Number.isFinite(anchor)) return raw;
    const e0 = raw[0].expected;
    const b0 = raw[0].best;
    const w0 = raw[0].worst;
    return raw.map((d, i) => {
      const expected = anchor + (d.expected - e0);
      const best = anchor + (d.best - b0);
      const worst = anchor + (d.worst - w0);
      return {
        ...d,
        expected,
        best,
        worst,
        bandHeight: Math.max(0, best - worst),
      };
    });
  }, [forecastData, batteryCapacityKwh, currentEnergyRemainingKwh]);

  if (readinessState === 'connecting' || readinessState === 'degraded') {
    return (
      <div className="rounded-lg border border-border bg-wt-bg-secondary p-4 flex items-center justify-center h-full min-h-[600px]">
        <div className="text-center text-sm text-wt-text-secondary">
          {readinessState === 'connecting' ? 'Setting up. Forecast when ready.' : 'Forecast unavailable. Data may be stale.'}
        </div>
      </div>
    );
  }

  // No data: still mount EnergyOutlook with empty chart and loading/error so chart never unmounts.
  if (!forecastData || !forecastData.chartData) {
    return (
      <div className="min-h-[600px]">
        <EnergyOutlook
          atSunriseExpected={0}
          atSunriseBest={0}
          atSunriseWorst={0}
          netDrawKw={netPower / 1000}
          timeToReserve={null}
          timeToSunrise={timeToSunrise}
          reserveKwh={reserveKwh}
          batteryCapacityKwh={batteryCapacityKwh}
          chartData={[]}
          sunriseTime=""
          sunsetTime=""
          confidenceLevel="medium"
          confidencePercent={50}
          depletionTimeExpected={null}
          depletionTimeBest={null}
          depletionTimeWorst={null}
          loading={loading}
          error={error}
          onRetry={onRetry}
          similarDayChartData={similarDay.chartData}
          similarDaySamplesUsed={similarDay.samplesUsed}
          similarDayConfidence={similarDay.confidence}
        />
      </div>
    );
  }

  const chartData = forecastData.chartData as {
    timestamps?: string[];
    socExpected?: number[];
    socBest?: number[];
    socWorst?: number[];
  };
  const sunriseTime = String(forecastData.sunriseTime ?? '');
  const sunsetTime = String(forecastData.sunsetTime ?? '');
  const sunriseDate = new Date(sunriseTime);
  const sunriseTs = sunriseDate.getTime();
  const timestamps = chartData.timestamps ?? [];
  // First index at or after sunrise (per spec); fallback to closest if none after
  const sunriseIdxAtOrAfter = timestamps.findIndex(
    (ts: string) => new Date(ts).getTime() >= sunriseTs
  );
  const sunriseIdxClosest =
    timestamps.length === 0
      ? -1
      : timestamps.reduce(
          (best: number, _ts: string, i: number) => {
            const ts = new Date(timestamps[i]).getTime();
            const bestTs = best >= 0 ? new Date(timestamps[best]).getTime() : Infinity;
            return Math.abs(ts - sunriseTs) < Math.abs(bestTs - sunriseTs) ? i : best;
          },
          -1 as number
        );
  const sunriseIdx =
    sunriseIdxAtOrAfter >= 0 ? sunriseIdxAtOrAfter : sunriseIdxClosest;
  const safeSunriseIdx = sunriseIdx >= 0 ? sunriseIdx : 0;
  // API returns soc as 0-100 (percent). If max <= 1.5 assume 0-1 decimal and scale to kWh without /100
  const socExpectedSample = chartData.socExpected ?? [];
  const socMax = socExpectedSample.length ? Math.max(...socExpectedSample) : 0;
  const isSocDecimal = socMax > 0 && socMax <= 1.5;
  const toKwh = (soc: number) =>
    isSocDecimal ? (soc ?? 0) * batteryCapacityKwh : ((soc ?? 0) * batteryCapacityKwh) / 100;
  const toPercent = (soc: number) => (isSocDecimal ? (soc ?? 0) * 100 : (soc ?? 0));

  // Use canonical forecast view model for sunrise values when available (eliminates duplicate computation).
  const vmValid = forecastViewModel && forecastViewModel.source !== 'unavailable' && forecastViewModel.isFresh;
  const atSunriseExpected = vmValid ? forecastViewModel.expectedAtSunriseKwh : toKwh(chartData.socExpected?.[safeSunriseIdx] ?? 0);
  const atSunriseBest = vmValid ? forecastViewModel.bestAtSunriseKwh : toKwh(chartData.socBest?.[safeSunriseIdx] ?? 0);
  const atSunriseWorst = vmValid ? forecastViewModel.worstAtSunriseKwh : toKwh(chartData.socWorst?.[safeSunriseIdx] ?? 0);
  const atSunrisePercent =
    chartData.socExpected?.[safeSunriseIdx] != null
      ? toPercent(chartData.socExpected[safeSunriseIdx])
      : undefined;

  return (
    <EnergyOutlook
      atSunriseExpected={atSunriseExpected}
      atSunriseBest={atSunriseBest}
      atSunriseWorst={atSunriseWorst}
      atSunrisePercent={atSunrisePercent}
      netDrawKw={netPower / 1000}
      timeToReserve={
        forecastData.depletionTimeExpected
          ? formatDistanceToNow(new Date(String(forecastData.depletionTimeExpected)), { addSuffix: false })
          : null
      }
      timeToSunrise={timeToSunrise}
      reserveKwh={reserveKwh}
      batteryCapacityKwh={batteryCapacityKwh}
      chartData={chartDataPoints}
      sunriseTime={sunriseTime}
      sunsetTime={sunsetTime}
      confidenceLevel={
        readinessState === 'warming_up'
          ? 'low'
          : readinessState === 'learning'
            ? ((forecastData.confidence as { level?: string })?.level === 'high' ? 'medium' : ((forecastData.confidence as { level?: string })?.level ?? 'medium'))
            : ((forecastData.confidence as { level?: string })?.level ?? 'medium')
      }
      confidencePercent={
        readinessState === 'warming_up'
          ? Math.min((forecastData.confidence as { score?: number })?.score ?? 50, 40)
          : readinessState === 'learning'
            ? Math.min((forecastData.confidence as { score?: number })?.score ?? 50, 70)
            : ((forecastData.confidence as { score?: number })?.score ?? 50)
      }
      depletionTimeExpected={
        forecastData.depletionTimeExpected
          ? format(new Date(String(forecastData.depletionTimeExpected)), 'h:mm a')
          : null
      }
      depletionTimeBest={
        forecastData.depletionTimeBest
          ? format(new Date(String(forecastData.depletionTimeBest)), 'h:mm a')
          : null
      }
      depletionTimeWorst={
        forecastData.depletionTimeWorst
          ? format(new Date(String(forecastData.depletionTimeWorst)), 'h:mm a')
          : null
      }
      loading={loading}
      error={error}
      onRetry={onRetry}
      similarDayChartData={similarDay.chartData}
      similarDaySamplesUsed={similarDay.samplesUsed}
      similarDayConfidence={similarDay.confidence}
      forecastWhy={forecastData.why ?? undefined}
    />
  );
}

export default function PropertyDashboard() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectProperty, selectedPropertyId, selectedPlantId, plants, loading: appLoading, selectPlant, properties, isHomeowner, organization, isInstaller } = useApp();
  const [resellerDialogOpen, setResellerDialogOpen] = useState(false);
  const [addGatewayOpen, setAddGatewayOpen] = useState(false);
  const [scheduleVisitOpen, setScheduleVisitOpen] = useState(false);
  const [addWarrantyOpen, setAddWarrantyOpen] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const { device: gatewayDevice, isLoading: gatewayLoading } = useGatewayDevice(propertyId);
  const { data: propertyVisits } = useVisits({ propertyId: propertyId ?? undefined });

  const propertyPlantTiers = plants
    .filter((p) => p.property_id === propertyId)
    .map((p) => (p as Record<string, unknown>).tier as string)
    .filter(Boolean);
  const { has: hasCap } = usePropertyCapabilities(propertyId, propertyPlantTiers);

  const data = usePropertyDashboardData(propertyId ?? undefined, selectedPlantId);
  const curtailment = useCurtailment(propertyId ?? undefined, selectedPlantId);

  const {
    telemetry,
    telemetryLoading,
    telemetryFetching,
    telemetryError: error,
    refetchTelemetry,
    pollStatus,
    pollLabel,
    lastPollError,
    capacityForUI,
    effectiveCapacityKwh,
    configuredCapacityKwh,
    readinessState,
    selectedProperty,
    selectedPlant,
    forecastRunwayData: forecastData,
    forecastRunwayLoading: showForecastLoading,
    forecastRunwayError: forecastError,
    refetchForecastRunway,
    timeToSunrise,
    systemRiskState,
    systemEnergyState,
    netPower,
    noTelemetry,
    overnightForecast,
    overnightForecastLoading,
    overnightForecastError,
    energyGuidance,
    energyGuidanceLoading,
    energyGuidanceError,
    resilienceRisk,
    resilienceSeverity,
  } = data;

  // Display battery state: always derive from physics (pv vs load) when we have pv/load, so we never
  // show "Discharging" when solar clearly exceeds load (e.g. plants-latest DB path uses raw sign only).
  const effectiveBatteryState = useMemo(() => {
    if (!telemetry) return undefined;
    const displayBattKw = normalizeBatteryPowerForDisplay(telemetry.battPower_w || 0);
    const pvKw = telemetry.pvPower_w / 1000;
    const loadKw = telemetry.loadPower_w / 1000;
    return getSystemEnergyStateDisplay(getBatteryStateForDisplay(displayBattKw, pvKw, loadKw));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derive from telemetry fields; omit full telemetry to avoid churn
  }, [telemetry?.battPower_w, telemetry?.pvPower_w, telemetry?.loadPower_w]);

  // Grid kW for dominant state and Energy Flow. When we're in reserve breach with ~0 kWh remaining
  // but the API says export, treat as import so we don't show "Exporting to Grid" (physics safeguard).
  const displayGridKw = useMemo(() => {
    const raw = getGridKwForDisplay(telemetry ?? undefined) ?? null;
    if (raw == null) return null;
    const energyRemainingKwh =
      telemetry && capacityForUI > 0 ? (telemetry.soc_pct / 100) * capacityForUI : 0;
    if (
      systemRiskState === 'action_required' &&
      energyRemainingKwh <= 0.1 &&
      raw > 0
    ) {
      return -Math.abs(raw);
    }
    return raw;
  }, [telemetry, capacityForUI, systemRiskState]);

  // When property.grid_mode is unset, infer from telemetry: grid power present → grid_primary for display (Backup Readiness card, etc.).
  const displayGridMode = selectedProperty?.grid_mode ?? (telemetry?.gridPower_w != null ? 'grid_primary' : null);

  const curtailmentKw = curtailment.status?.is_curtailing_now
    ? curtailment.status.wasted_power_w / 1000
    : undefined;

  const dominantState = useDominantPowerState({
    generatorKw: telemetry?.genPower_w != null ? telemetry.genPower_w / 1000 : null,
    gridKw: displayGridKw,
    batteryKw: telemetry ? normalizeBatteryPowerForDisplay(telemetry.battPower_w || 0) : 0,
    batteryStateOverride: effectiveBatteryState,
    pvKw: telemetry ? (telemetry.pvPower_w || 0) / 1000 : 0,
    socPct: telemetry?.soc_pct,
    curtailmentKw,
  });

  // Canonical forecast view model — single source of truth for OvernightForecastCard, EnergyOutlook, EnergyGuidance.
  const reserveThresholdKwh = ((selectedProperty?.battery_reserve_pct ?? 20) / 100) * capacityForUI;
  const forecastViewModel = useMemo(() => buildDashboardForecastViewModel({
    forecastRunway: forecastData,
    forecastRunwayLoading: showForecastLoading,
    forecastRunwayError: forecastError,
    overnightForecast: overnightForecast ?? null,
    energyGuidance: energyGuidance ?? null,
    capacityKwh: capacityForUI,
    reserveThresholdKwh,
    currentSocPct: telemetry?.soc_pct ?? null,
  }), [forecastData, showForecastLoading, forecastError, overnightForecast, energyGuidance, capacityForUI, reserveThresholdKwh, telemetry?.soc_pct]);

  // Derive runwaySunriseKwh from the view model for OvernightForecastCard.
  const runwaySunriseKwh = forecastViewModel.source === 'forecast-runway'
    ? { expected: forecastViewModel.expectedAtSunriseKwh, best: forecastViewModel.bestAtSunriseKwh, worst: forecastViewModel.worstAtSunriseKwh }
    : null;

  // Dev logging
  useEffect(() => {
    logForecastViewModel(forecastViewModel, {
      guidanceDecision: energyGuidance ? {
        state: energyGuidance.state,
        forecastLowerKwh: energyGuidance.forecast_lower_kwh,
        reserveKwh: energyGuidance.reserve_kwh,
      } : undefined,
    });
  }, [forecastViewModel, energyGuidance]);

  const loading = telemetryLoading && !telemetry;

  useEffect(() => {
    if (propertyId) addRecentPropertyId(propertyId);
  }, [propertyId]);

  const [polling, setPolling] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    if (propertyId && selectedPropertyId !== propertyId) selectProperty(propertyId);
  }, [propertyId, selectedPropertyId, selectProperty]);

  useEffect(() => {
    if (propertyId && plants.length > 0 && !selectedPlantId) {
      const propertyPlants = plants.filter((p) => p.property_id === propertyId);
      if (propertyPlants.length > 0) selectPlant(propertyPlants[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync plant when property/plants change; omit selectPlant to avoid loop
  }, [propertyId, plants, selectedPlantId]);

  const handleBackfill = useCallback(async () => {
    if (!selectedPlantId) return;
    setBackfilling(true);
    try {
      toast.info('Starting backfill...', { description: 'Importing historical data.' });
      const { data: backfillResult, error: backfillError } = await supabase.functions.invoke('systems-backfill', {
        body: { plantId: selectedPlantId, days: 30 },
      });
      if (backfillError) throw backfillError;
      if (backfillResult?.mode === 'background') {
        toast.success('Backfill started', { description: backfillResult.message });
      } else if (backfillResult?.recordsInserted > 0) {
        toast.success(`Imported ${backfillResult.recordsInserted} records`);
      } else {
        toast.warning('No historical data found');
      }
      await new Promise((r) => setTimeout(r, 2000));
      await refetchTelemetry();
    } catch (err) {
      console.error('Error during backfill:', err);
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  }, [selectedPlantId, refetchTelemetry]);

  const handlePollNow = useCallback(async () => {
    if (!propertyId) return;
    setPolling(true);
    try {
      await supabase.functions.invoke('poll-all', { body: { propertyId } });
      await new Promise((r) => setTimeout(r, 2000));
      await refetchTelemetry();
      await refetchForecastRunway();
      await queryClient.invalidateQueries({ queryKey: ['property-poll-status', propertyId] });
    } catch (err) {
      console.error('Error polling:', err);
    } finally {
      setPolling(false);
    }
  }, [propertyId, refetchTelemetry, refetchForecastRunway, queryClient]);

  // Full-page loader only until app-context resolves. Shell + skeletons show once we have a plant.
  if (appLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-secondary" />
          <span className="text-xs text-secondary">Loading...</span>
        </div>
      </div>
    );
  }

  if (!selectedPlantId) {
    const propertyPlants = plants.filter(p => p.property_id === propertyId);
    
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md text-center bg-wt-bg-secondary border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-secondary">No Plant Configured</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-secondary">
              {propertyPlants.length === 0 
                ? "No plants or equipment configured yet."
                : "Select a plant to view data."}
            </p>
            <Button size="sm" onClick={() => navigate(`/app/onboarding?propertyId=${propertyId}`)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Plant
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (noTelemetry && !telemetry) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">{selectedPlant?.display_name ?? 'Dashboard'}</h1>
        </div>

        <Card className="max-w-lg mx-auto text-center bg-wt-bg-secondary border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-center gap-2 text-secondary">
              <Download className="w-4 h-4 text-secondary" />
              {isHomeowner ? 'No system data' : 'No Telemetry'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-secondary">
              {isHomeowner ? 'Import history to see past data.' : 'Run a backfill to import historical data.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button 
                size="sm"
                onClick={handleBackfill}
                disabled={backfilling}
              >
                <Download className={`w-3.5 h-3.5 mr-1.5 ${backfilling ? 'animate-bounce' : ''}`} />
                {backfilling ? 'Importing...' : 'Import History'}
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={handlePollNow}
                disabled={polling}
              >
                <Zap className={`w-3.5 h-3.5 mr-1.5 ${polling ? 'animate-pulse' : ''}`} />
                {polling ? (isHomeowner ? 'Updating…' : 'Polling...') : (isHomeowner ? 'Refresh Data' : 'Poll Now')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5 px-6 py-5">
      {/* Header: title + one persistent status line (Live / Updated / Updating) for legibility */}
      <div className="flex items-center justify-between max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-base font-semibold text-foreground">{selectedPlant?.display_name ?? 'Dashboard'}</h1>
          <span className="text-wt-text-secondary text-xs select-none" aria-hidden="true">·</span>
          {/* Single stable status: always show something readable (design: avoid "loading then nothing") */}
          {((telemetryFetching && telemetry) || showForecastLoading) ? (
            <span className="text-sm text-wt-text-secondary flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-wt-green-primary animate-pulse" aria-hidden="true" />
              Updating…
            </span>
          ) : pollStatus === 'offline' ? (
            <span className={`text-sm ${getPollStatusDisplay('offline', isHomeowner).className} inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5`}>
              <span className={`w-1.5 h-1.5 rounded-full ${getPollStatusDisplay('offline', isHomeowner).dot}`} />
              {pollStatusOfflineCopy(!!isHomeowner)}
            </span>
          ) : (
            <>
              <StatusBadge status={pollStatus} isHomeowner={isHomeowner} />
              <span className="text-sm text-wt-text-secondary" title={pollLabel}>
                {pollLabel}
              </span>
              {telemetry?.last_updated && (
                <span className="text-sm text-wt-text-secondary">
                  · Data {formatDistanceToNow(new Date(telemetry.last_updated), { addSuffix: true })}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => refetchTelemetry()}
            disabled={telemetryFetching}
            className="h-8 px-2.5 text-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${telemetryFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            size="sm" 
            onClick={handlePollNow}
            disabled={polling}
            className="h-8 px-2.5 text-sm"
          >
            <Zap className={`w-3 h-3 mr-1 ${polling ? 'animate-pulse' : ''}`} />
            {isHomeowner ? 'Refresh' : 'Poll'}
          </Button>
        </div>
      </div>

      {/* Canonical readiness banner: single calm banner when not ready — metric-aware, severity-colored */}
      {showReadinessBanner(readinessState) && (() => {
        const rc = readinessCopy(readinessState as 'provisional' | 'connecting' | 'warming_up' | 'learning' | 'degraded');
        const bannerColor = rc.severity === 'error'
          ? 'bg-destructive/10 border-destructive/20 text-destructive'
          : rc.severity === 'warning'
            ? 'bg-wt-amber-primary/10 border-wt-amber-primary/20 text-wt-amber-primary'
            : 'bg-wt-slate-blue/10 border-wt-slate-blue/20 text-wt-text-secondary';
        return (
          <div
            className={`max-w-[1400px] mx-auto p-2.5 rounded-lg border text-sm ${bannerColor}`}
            role="status"
          >
            {rc.message}
          </div>
        );
      })()}

      {error && (
        <div className="p-2.5 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}

      {selectedProperty && selectedProperty.latitude == null && (
        <div className="max-w-[1400px] mx-auto p-2.5 rounded-lg border text-sm bg-wt-amber-primary/10 border-wt-amber-primary/20 text-wt-amber-primary">
          Add an address to enable weather forecasting and load predictions.{' '}
          <Link to={`/app/settings?tab=property&propertyId=${propertyId}`} className="underline font-medium">
            Update in Settings
          </Link>
        </div>
      )}

      {/* Dashboard Grid Layout */}
      {loading && !telemetry ? (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 max-w-[1400px] mx-auto auto-rows-auto">
          <div className="flex flex-col gap-5">
            <SkeletonEnergyPosition />
            <SkeletonEnergyFlow />
          </div>
          <SkeletonChart className="min-h-[600px]" />
        </div>
      ) : telemetry ? (
        /* Forecast + evidence: one narrative block; tight spacing so Outlook and Telemetry read as one story. */
        <div className="max-w-[1400px] mx-auto space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 auto-rows-auto">
          {/* Left Column */}
          <div className="flex flex-col gap-5">
            {/* Energy Position — gated by readiness; dominant state replaces Net Power */}
            <EnergyPosition
              energyRemainingKwh={
                (telemetry.soc_pct / 100) * capacityForUI
              }
              usableKwh={
                Math.max(
                  0,
                  (telemetry.soc_pct / 100) * capacityForUI -
                    ((selectedProperty?.battery_reserve_pct ?? 20) / 100) * capacityForUI
                )
              }
              reserveKwh={
                ((selectedProperty?.battery_reserve_pct ?? 20) / 100) * capacityForUI
              }
              totalCapacityKwh={capacityForUI}
              effectiveCapacityKwh={effectiveCapacityKwh}
              configuredCapacityKwh={configuredCapacityKwh}
              timeToSunrise={timeToSunrise}
              dominantState={dominantState}
              forecastData={
                !forecastError &&
                forecastData?.chartData?.socWorst?.length
                  ? {
                      worstCase:
                        Math.min(...(forecastData.chartData?.socWorst ?? [])) * (capacityForUI / 100),
                    }
                  : undefined
              }
              readinessState={readinessState}
              systemRiskState={systemRiskState}
            />

            {selectedPlantId && (
              <Link
                to={`/app/battery-health/${selectedPlantId}`}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View Battery Health →
              </Link>
            )}

            {/* Energy Flow — Home = on-site consumption only for providers that report load = house + export (e.g. Sol-Ark) */}
            <EnergyFlow
              solarKw={(telemetry.pvPower_w || 0) / 1000}
              batteryKw={normalizeBatteryPowerForDisplay(telemetry.battPower_w || 0)}
              loadKw={getLoadKwForEnergyFlow(
                (telemetry.loadPower_w || 0) / 1000,
                displayGridKw,
                selectedPlant?.provider_type ?? null
              )}
              gridKw={displayGridKw}
              genKw={telemetry.genPower_w ? telemetry.genPower_w / 1000 : null}
              batteryStateOverride={effectiveBatteryState}
            />

            {selectedPlant?.tier === 'monitor' ? (
              <Card className="bg-wt-bg-secondary border-border overflow-hidden">
                <div className="h-24 bg-gradient-to-b from-wt-amber-primary/5 to-transparent flex items-center justify-center">
                  <Moon className="h-8 w-8 text-wt-amber-primary/30" />
                </div>
                <CardContent className="pt-3 space-y-3">
                  <p className="text-sm font-medium text-foreground">Overnight Forecast & Alerts</p>
                  <ul className="text-sm text-wt-text-secondary space-y-1.5">
                    <li>See overnight depletion risk 6+ hours in advance</li>
                    <li>Get alerts for reserve breach, system offline, low battery</li>
                    <li>Energy guidance: when to use energy freely vs conserve</li>
                  </ul>
                  <p className="text-sm text-wt-text-secondary">
                    Alert tier: <span className="font-mono-numeric text-foreground">$7</span>/system/month
                  </p>
                  <Button size="sm" asChild>
                    <Link to="/app/billing">Upgrade to Alert</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <OvernightForecastCard
                  data={overnightForecast ?? null}
                  loading={overnightForecastLoading}
                  error={overnightForecastError ?? null}
                  readinessState={readinessState}
                  gridMode={displayGridMode}
                  availableKwh={
                    telemetry && capacityForUI > 0
                      ? (telemetry.soc_pct / 100) * capacityForUI
                      : null
                  }
                  loadKw={telemetry?.loadPower_w != null ? telemetry.loadPower_w / 1000 : null}
                  resilienceRisk={resilienceRisk}
                  resilienceSeverity={resilienceSeverity}
                  runwaySunriseKwh={runwaySunriseKwh}
                />
                <EnergyGuidanceCard
                  data={energyGuidance ?? null}
                  loading={energyGuidanceLoading}
                  error={energyGuidanceError ?? null}
                  readinessState={readinessState}
                  systemRiskState={systemRiskState}
                  forecastViewModel={forecastViewModel}
                />
              </>
            )}
            <CurtailmentCard
              status={curtailment.status}
              loading={curtailment.statusLoading}
              error={curtailment.statusError}
              isHomeowner={isHomeowner}
              rateCentsPerKwh={properties.find((p) => p.id === propertyId)?.electricity_rate_cents_per_kwh ?? undefined}
            />
            <CurtailmentSummary
              summary={curtailment.summary}
              loading={curtailment.dailyLoading}
              error={curtailment.dailyError}
            />
            {/* Gateway status card — installer only */}
            {isInstaller && (
              <>
                <GatewayStatusCard
                  propertyId={propertyId!}
                  device={gatewayDevice}
                  isLoading={gatewayLoading}
                  onAddGateway={() => setAddGatewayOpen(true)}
                />
                <AddGatewayDialog
                  open={addGatewayOpen}
                  onOpenChange={setAddGatewayOpen}
                  propertyId={propertyId!}
                  propertyName={properties.find(p => p.id === propertyId)?.name ?? 'Property'}
                />
              </>
            )}
            {/* Billing card: homeowner sees billing plan, installer sees setup/status */}
            {(() => {
              // Get billing fields from the full property object in context
              const fullProp = properties.find(p => p.id === propertyId);
              const billingMode = fullProp?.billing_mode;
              const resellerStatus = fullProp?.reseller_billing_status;
              const customerPrice = fullProp?.customer_price_cents;

              return (
                <>
                  {isHomeowner && billingMode === 'reseller' && resellerStatus !== 'none' && (
                    <HomeownerBillingCard
                      propertyId={propertyId!}
                      priceCents={customerPrice ?? null}
                      status={resellerStatus ?? 'none'}
                      companyName={organization?.name}
                    />
                  )}
                  {isInstaller && organization?.stripe_connect_charges_enabled && billingMode !== 'reseller' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setResellerDialogOpen(true)}
                        className="w-full"
                      >
                        Start billing this customer
                      </Button>
                      <ResellerSetupDialog
                        open={resellerDialogOpen}
                        onOpenChange={setResellerDialogOpen}
                        propertyId={propertyId!}
                        propertyName={fullProp?.name ?? 'Property'}
                        plantTiers={plants.filter(p => p.property_id === propertyId).map(p => p.tier)}
                        defaultPriceCents={organization?.default_customer_price_cents ?? null}
                      />
                    </>
                  )}
                  {isInstaller && billingMode === 'reseller' && (
                    <Card className="bg-wt-bg-secondary border-border">
                      <CardContent className="pt-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Customer billing</span>
                          <Badge variant="outline" className={
                            resellerStatus === 'active'
                              ? 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20'
                              : resellerStatus === 'past_due'
                                ? 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20'
                                : resellerStatus === 'pending'
                                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                  : 'bg-destructive/10 text-destructive border-destructive/20'
                          }>
                            {resellerStatus === 'active' ? 'Active' :
                             resellerStatus === 'past_due' ? 'Past Due' :
                             resellerStatus === 'pending' ? 'Pending' :
                             resellerStatus === 'trialing' ? 'Trial' :
                             resellerStatus === 'canceled' ? 'Canceled' : 'None'}
                          </Badge>
                        </div>
                        {customerPrice != null && (
                          <p className="text-sm text-foreground font-mono">
                            ${(customerPrice / 100).toFixed(2)}/mo
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            })()}
            {/* Service History — installer only */}
            {isInstaller && (
              <>
                <Card className="bg-wt-bg-secondary border-border">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">Service History</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => setScheduleVisitOpen(true)}>
                        <Plus className="w-3 h-3 mr-1" />
                        Schedule
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(propertyVisits ?? []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No visits recorded</p>
                    ) : (
                      (propertyVisits ?? []).slice(0, 5).map(v => (
                        <Link
                          key={v.id}
                          to={`/app/visits/${v.id}`}
                          className="flex items-center justify-between text-xs hover:bg-muted/50 rounded px-2 py-1.5 -mx-2"
                        >
                          <span className="truncate">
                            {v.visit_type.replaceAll('_', ' ')} — {v.technician_name ?? 'Unassigned'}
                          </span>
                          <Badge variant="outline" className="text-[10px] ml-2 shrink-0">
                            {v.status.replace('_', ' ')}
                          </Badge>
                        </Link>
                      ))
                    )}
                    {(propertyVisits ?? []).length > 5 && (
                      <Link to={`/app/visits?property=${propertyId}`} className="text-xs text-primary hover:underline">
                        View all visits
                      </Link>
                    )}
                  </CardContent>
                </Card>
                <ScheduleVisitDialog
                  open={scheduleVisitOpen}
                  onOpenChange={setScheduleVisitOpen}
                  preselectedPropertyId={propertyId}
                />
              </>
            )}
            {/* Warranty Registry — installer only */}
            {isInstaller && propertyId && (
              <>
                <WarrantyRegistryCard propertyId={propertyId} onAddClick={() => setAddWarrantyOpen(true)} />
                <AddWarrantyDialog
                  open={addWarrantyOpen}
                  onOpenChange={setAddWarrantyOpen}
                  propertyId={propertyId}
                  plantId={selectedPlantId}
                />
              </>
            )}
            {/* Customer Reports — service provider admins only */}
            {isInstaller && propertyId && (
              <CustomerReportSettings propertyId={propertyId} />
            )}
          </div>

          {/* Right Column — Energy Outlook then Telemetry (evidence); stacked so Telemetry is directly under Outlook. Monitor tier: hide forecast column; show CTA. */}
          <div className="min-h-0 flex flex-col gap-4">
            {selectedPlant?.tier === 'monitor' ? (
              <Card className="bg-wt-bg-secondary border-border overflow-hidden min-h-[300px]">
                <div className="h-48 bg-gradient-to-b from-wt-green-primary/5 to-transparent flex items-center justify-center">
                  <div className="w-3/4 h-24 rounded border border-dashed border-wt-text-secondary/20 flex items-center justify-center">
                    <p className="text-xs text-wt-text-secondary/50">24h forecast chart</p>
                  </div>
                </div>
                <CardContent className="pt-3 space-y-3">
                  <p className="text-sm font-medium text-foreground">Energy Outlook</p>
                  <ul className="text-sm text-wt-text-secondary space-y-1.5">
                    <li>24-hour battery forecast with confidence bands</li>
                    <li>Expected, best-case, and worst-case scenarios</li>
                    <li>Sunrise/sunset markers and reserve threshold</li>
                  </ul>
                  <p className="text-sm text-wt-text-secondary">
                    Alert tier: <span className="font-mono-numeric text-foreground">$7</span>/system/month
                  </p>
                  <Button size="sm" asChild>
                    <Link to="/app/billing">Upgrade to Alert</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <ErrorBoundary>
                <ForecastRightColumn
                  propertyId={propertyId ?? undefined}
                  selectedPlantId={selectedPlantId}
                  forecastData={forecastData}
                  timeToSunrise={timeToSunrise}
                  netPower={netPower}
                  selectedProperty={selectedProperty}
                  batteryCapacityKwh={capacityForUI}
                  currentEnergyRemainingKwh={telemetry ? (telemetry.soc_pct / 100) * capacityForUI : undefined}
                  readinessState={readinessState}
                  loading={showForecastLoading}
                  error={forecastError}
                  onRetry={refetchForecastRunway}
                  forecastViewModel={forecastViewModel}
                />
              </ErrorBoundary>
            )}
            {/* Optimization Insights — recommendations + curtailment fallback */}
            {propertyId && (isInstaller || hasCap('upgrade_opportunities_homeowner')) && (
              <OptimizationInsightsCard
                propertyId={propertyId}
                isOperator={isInstaller}
                curtailmentKwh={curtailment.summary.wasted30dKwh}
                curtailmentDollars={(() => {
                  const rate = properties.find((p) => p.id === propertyId)?.electricity_rate_cents_per_kwh;
                  const kwh = curtailment.summary.wasted30dKwh;
                  return rate && kwh ? kwh * rate / 100 : 0;
                })()}
              />
            )}
            {/* Telemetry directly under Outlook; muted label, no extra top padding. */}
            <section className="pt-0 shrink-0" aria-label={t('telemetryTimeline', !!isHomeowner)}>
              <p className="text-sm text-wt-text-secondary uppercase tracking-[0.05em] mb-2 font-medium">
                {t('telemetryTimeline', !!isHomeowner)}
              </p>
            <TelemetryTimeline
              propertyId={propertyId!}
              plantId={selectedPlantId ?? undefined}
              timezone={selectedProperty?.timezone}
              configuredCapacityKwh={capacityForUI}
              reserveKwh={
                ((selectedProperty?.battery_reserve_pct ?? 20) / 100) * capacityForUI
              }
              currentValues={{
                energy: telemetry ? (telemetry.soc_pct / 100) * capacityForUI : undefined,
                  solar: telemetry ? (telemetry.pvPower_w || 0) / 1000 : undefined,
                  load: telemetry ? (telemetry.loadPower_w || 0) / 1000 : undefined,
                  battery: telemetry ? normalizeBatteryPowerForDisplay(telemetry.battPower_w || 0) : undefined,
                  soc: telemetry?.soc_pct,
                }}
              />
            </section>
          </div>
          </div>

          {/* SoC Mismatch Warning — installer-only diagnostic */}
          {!isHomeowner && telemetry?.socMismatch && (
            <div className="p-2.5 rounded-lg bg-wt-amber-primary/10 border border-wt-amber-primary/20 text-wt-amber-primary text-sm">
              <strong>SoC Mismatch:</strong> Equipment differs by &gt;5%. Check System page.
            </div>
          )}

          {/* Homeowner service plan card + issue submission */}
          {isHomeowner && propertyId && (
            <>
              <HomeownerPlanCard propertyId={propertyId} />
              <HomeownerServiceActivity propertyId={propertyId} />
              {hasCap('issue_submission') && (
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setReportIssueOpen(true)}
                  >
                    Report an Issue
                  </Button>
                  <MyReportedIssues propertyId={propertyId} />
                </div>
              )}
              <ReportIssueDialog
                open={reportIssueOpen}
                onOpenChange={setReportIssueOpen}
                propertyId={propertyId}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['my-reported-issues', propertyId] });
                }}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
